import { LoadingWrapper } from "@/components/shared/LoadingWrapper";
import { IConvertToTaskProps } from "@/interfaces/IRecap";
import { formatServerDate } from "@/middleware/Date";
import { useTheme } from "@/store/selectors/AppSelectors";
import { Flex, Modal } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const ConvertToTask = ({
  actionItem,
  open,
  sessionId,
  onCloseModal,
  onSaveStatus,
}: IConvertToTaskProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { t, i18n } = useTranslation();

  const theme = useTheme();

  const baseUrl = useMemo(() => {
    const baseApiUrl =
      (window as any)._env_?.VITE_BASE_API_URL ||
      import.meta.env.VITE_BASE_API_URL ||
      "";

    return baseApiUrl
      .replace("/meet/api", "")
      .replace("/meet/", "")
      .replace(/\/$/, "");
  }, []);

  const iframeUrl = useMemo(() => {
    const path = "/task/modify-task-standalone";

    const params = new URLSearchParams({
      culture: i18n.language === "fa-IR" ? "fa-IR" : "en-US",
      theme: theme === "dark" ? "dark" : "light",
    });

    return `${baseUrl}${path}?${params.toString()}`;
  }, [baseUrl, i18n.language, theme]);

  useEffect(() => {
    if (!onCloseModal) return;
    (window as any).onCloseModal = onCloseModal;

    if (!onSaveStatus) return;
    (window as any).onSaveStatus = onSaveStatus;
  }, [onCloseModal, onSaveStatus]);

  const createPriorityPayload = (priority: string | number) => {
    switch (typeof priority) {
      case "string":
        return 2;

      default:
        return parseInt((actionItem?.priority || 0).toString());
    }
  };

  useEffect(() => {
    const iframeElement =
      document.querySelector<HTMLIFrameElement>("#convert-task-form");

    if (!iframeElement?.contentWindow) return;
    const message = {
      title: actionItem?.title ?? "",
      assignee: actionItem?.user ?? null,
      actionItemId: actionItem?.id ?? "",
      priority: createPriorityPayload(actionItem?.priority),
      dueDate: formatServerDate(actionItem?.dueDate),
      description: actionItem?.description ?? "",
      sessionId,
      isRecap: true,
    };

    const sendMessage = () => {
      iframeRef.current?.contentWindow?.postMessage(message, "*");
    };

    if (isLoaded) sendMessage();

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "READY") sendMessage();
      if (event.data?.type === "onCloseModal") onCloseModal?.();
      if (event.data?.type === "onSaveStatus") {
        const { type, text } = event?.data?.payload || {};
        onSaveStatus?.(type, text);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isLoaded, actionItem, sessionId]);

  useEffect(() => {
    if (open) {
      setIsLoaded(false);
    }
  }, [open]);

  return (
    <Modal
      width={900}
      centered
      title={t("recap.convertToTask")}
      open={open}
      onCancel={onCloseModal}
      footer={null}
      destroyOnHidden
    >
      <div className="convert-task-container">
        {!isLoaded && (
          <Flex
            align="center"
            justify="center"
            className="convert-task-loading"
          >
            <LoadingWrapper notFixed transparent />
          </Flex>
        )}

        <iframe
          ref={iframeRef}
          key={iframeUrl}
          src={iframeUrl}
          className="convert-task-form"
          id="convert-task-form"
          onLoad={() => setIsLoaded(true)}
          allowFullScreen
          sandbox="allow-same-origin allow-scripts allow-popups allow-downloads allow-forms allow-top-navigation-by-user-activation"
          allow="clipboard-read; clipboard-write *"
        />
      </div>
    </Modal>
  );
};

export default ConvertToTask;
